import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useTaskChecklistStore,
  CHECKLIST_TITLE_MAX,
  CHECKLIST_INTERVAL_HINT_MAX,
  type ChecklistItemDto,
  type CreateChecklistItemRequest,
} from '../../../store/useTaskChecklistStore';
import { useToastStore } from '../../../store/useToastStore';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { Badge } from '../../../components/Shared/Badge';
import { ConfirmDialog } from '../../../components/Shared/ConfirmDialog';
import { Plus, Trash2, Pencil, Camera, ChevronUp, ChevronDown, Check, X } from 'lucide-react';

/** A checklist item that only exists in the form — the task has no id yet. */
export interface PendingChecklistItem extends CreateChecklistItemRequest {
  _localId: string;
}

interface ChecklistEditorProps {
  /** Edit mode: items are loaded from and written to the API. */
  taskId?: string;
  /** Create mode: pending items held by the parent form. */
  pendingItems?: PendingChecklistItem[];
  onPendingItemsChange?: (items: PendingChecklistItem[]) => void;
}

let localIdCounter = 0;
const nextLocalId = () => `pending-checklist-${++localIdCounter}`;

const emptyDraft = { title: '', intervalHint: '', requiresPhoto: false };

export function ChecklistEditor({ taskId, pendingItems, onPendingItemsChange }: ChecklistEditorProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const isEditMode = !!taskId;
  const { items, loading, fetchItems, createItem, updateItem, deleteItem, reorderItems } =
    useTaskChecklistStore();
  const { addToast } = useToastStore();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (taskId) {
      fetchItems(taskId);
    }
  }, [taskId, fetchItems]);

  const displayItems: Array<ChecklistItemDto | PendingChecklistItem> = isEditMode
    ? items
    : pendingItems ?? [];

  const validate = (title: string, intervalHint: string): boolean => {
    if (!title.trim()) {
      addToast(t('tasks:checklist.validation.titleRequired'), 'error');
      return false;
    }
    if (title.trim().length > CHECKLIST_TITLE_MAX) {
      addToast(t('tasks:checklist.validation.titleTooLong', { max: CHECKLIST_TITLE_MAX }), 'error');
      return false;
    }
    if (intervalHint.trim().length > CHECKLIST_INTERVAL_HINT_MAX) {
      addToast(
        t('tasks:checklist.validation.intervalHintTooLong', { max: CHECKLIST_INTERVAL_HINT_MAX }),
        'error',
      );
      return false;
    }
    return true;
  };

  const closeAdd = () => {
    setDraft(emptyDraft);
    setIsAddOpen(false);
  };

  const handleAdd = async () => {
    if (!validate(draft.title, draft.intervalHint)) return;

    const payload: CreateChecklistItemRequest = {
      title: draft.title.trim(),
      intervalHint: draft.intervalHint.trim() || null,
      requiresPhoto: draft.requiresPhoto,
    };

    if (isEditMode && taskId) {
      try {
        await createItem(taskId, payload);
        addToast(t('tasks:checklist.messages.created'), 'success');
      } catch {
        addToast(t('tasks:checklist.messages.createError'), 'error');
        return;
      }
    } else if (onPendingItemsChange) {
      onPendingItemsChange([...(pendingItems ?? []), { ...payload, _localId: nextLocalId() }]);
    }
    closeAdd();
  };

  const startEdit = (item: ChecklistItemDto) => {
    setEditingId(item.id ?? null);
    setEditDraft({
      title: item.title ?? '',
      intervalHint: item.intervalHint ?? '',
      requiresPhoto: item.requiresPhoto ?? false,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyDraft);
  };

  const handleSaveEdit = async (item: ChecklistItemDto) => {
    if (!taskId || !item.id) return;
    if (!validate(editDraft.title, editDraft.intervalHint)) return;

    try {
      await updateItem(taskId, item.id, {
        title: editDraft.title.trim(),
        intervalHint: editDraft.intervalHint.trim() || null,
        requiresPhoto: editDraft.requiresPhoto,
        isActive: item.isActive ?? true,
      });
      addToast(t('tasks:checklist.messages.updated'), 'success');
      cancelEdit();
    } catch {
      addToast(t('tasks:checklist.messages.updateError'), 'error');
    }
  };

  const toggleActive = async (item: ChecklistItemDto) => {
    if (!taskId || !item.id) return;
    try {
      await updateItem(taskId, item.id, {
        title: item.title ?? '',
        intervalHint: item.intervalHint ?? null,
        requiresPhoto: item.requiresPhoto ?? false,
        isActive: !(item.isActive ?? true),
      });
    } catch {
      addToast(t('tasks:checklist.messages.updateError'), 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    if (isEditMode && taskId) {
      try {
        await deleteItem(taskId, deleteId);
        addToast(t('tasks:checklist.messages.deleted'), 'success');
      } catch {
        addToast(t('tasks:checklist.messages.deleteError'), 'error');
      }
    } else if (onPendingItemsChange) {
      onPendingItemsChange((pendingItems ?? []).filter((i) => i._localId !== deleteId));
    }
    setDeleteId(null);
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= displayItems.length) return;

    if (isEditMode && taskId) {
      const ids = items.map((i) => i.id).filter((id): id is string => !!id);
      if (ids.length !== items.length) return;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      try {
        await reorderItems(taskId, ids);
      } catch {
        addToast(t('tasks:checklist.messages.reorderError'), 'error');
      }
      return;
    }

    if (onPendingItemsChange && pendingItems) {
      const next = [...pendingItems];
      [next[index], next[target]] = [next[target], next[index]];
      onPendingItemsChange(next);
    }
  };

  const rowKey = (item: ChecklistItemDto | PendingChecklistItem) =>
    '_localId' in item ? item._localId : item.id!;

  return (
    <div className="space-y-2">
      {displayItems.length === 0 && !loading ? (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          {t('tasks:checklist.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {displayItems.map((item, index) => {
            const isPending = '_localId' in item;
            const isEditingRow = !isPending && editingId === item.id;
            const isInactive = !isPending && item.isActive === false;

            return (
              <div
                key={rowKey(item)}
                className={`py-2 px-3 rounded-md border ${
                  isInactive
                    ? 'bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-70'
                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
                {isEditingRow ? (
                  <div className="space-y-2">
                    <Input
                      value={editDraft.title}
                      onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                      placeholder={t('tasks:checklist.fields.titlePlaceholder')}
                      maxLength={CHECKLIST_TITLE_MAX}
                    />
                    <Input
                      value={editDraft.intervalHint}
                      onChange={(e) => setEditDraft({ ...editDraft, intervalHint: e.target.value })}
                      placeholder={t('tasks:checklist.fields.intervalHintPlaceholder')}
                      maxLength={CHECKLIST_INTERVAL_HINT_MAX}
                    />
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={editDraft.requiresPhoto}
                        onChange={(e) =>
                          setEditDraft({ ...editDraft, requiresPhoto: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-gray-900 dark:text-white">
                        {t('tasks:checklist.fields.requiresPhoto')}
                      </span>
                    </label>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={cancelEdit}
                        className="!text-xs !py-1 !px-2"
                      >
                        <X className="w-3 h-3 mr-1" />
                        {t('common:actions.cancel')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleSaveEdit(item)}
                        disabled={loading}
                        className="!text-xs !py-1 !px-2"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        {t('common:actions.save')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-gray-400 tabular-nums w-5 flex-shrink-0">
                        {index + 1}.
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                        {item.title}
                      </span>
                      {item.intervalHint && (
                        <Badge variant="default" className="flex-shrink-0">
                          {item.intervalHint}
                        </Badge>
                      )}
                      {item.requiresPhoto && (
                        <span
                          className="flex-shrink-0 text-amber-600"
                          title={t('tasks:checklist.fields.requiresPhoto')}
                        >
                          <Camera className="w-3.5 h-3.5" />
                        </span>
                      )}
                      {isInactive && (
                        <Badge variant="default" className="flex-shrink-0">
                          {t('tasks:checklist.inactive')}
                        </Badge>
                      )}
                      {isPending && (
                        <Badge variant="warning" className="flex-shrink-0">
                          {t('tasks:checklist.newBadge')}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => move(index, -1)}
                        disabled={index === 0 || loading}
                        title={t('tasks:checklist.actions.moveUp')}
                        className="p-1"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => move(index, 1)}
                        disabled={index === displayItems.length - 1 || loading}
                        title={t('tasks:checklist.actions.moveDown')}
                        className="p-1"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </Button>
                      {!isPending && (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(item)}
                            title={t('common:actions.edit')}
                            className="p-1"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActive(item)}
                            title={
                              isInactive
                                ? t('tasks:checklist.actions.activate')
                                : t('tasks:checklist.actions.deactivate')
                            }
                            className="p-1 text-xs"
                          >
                            {isInactive
                              ? t('tasks:checklist.actions.activate')
                              : t('tasks:checklist.actions.deactivate')}
                          </Button>
                        </>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteId(rowKey(item))}
                        title={t('common:actions.delete')}
                        className="p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isAddOpen ? (
        <Button type="button" variant="secondary" size="sm" onClick={() => setIsAddOpen(true)}>
          <Plus className="w-4 h-4 mr-1" />
          {t('tasks:checklist.actions.addItem')}
        </Button>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50/50 dark:bg-gray-800/40 space-y-2">
          <Input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder={t('tasks:checklist.fields.titlePlaceholder')}
            maxLength={CHECKLIST_TITLE_MAX}
          />
          <Input
            value={draft.intervalHint}
            onChange={(e) => setDraft({ ...draft, intervalHint: e.target.value })}
            placeholder={t('tasks:checklist.fields.intervalHintPlaceholder')}
            maxLength={CHECKLIST_INTERVAL_HINT_MAX}
          />
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={draft.requiresPhoto}
              onChange={(e) => setDraft({ ...draft, requiresPhoto: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-gray-900 dark:text-white">
              {t('tasks:checklist.fields.requiresPhoto')}
            </span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('tasks:checklist.fields.requiresPhotoHint')}
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={closeAdd}
              className="!text-xs !py-1 !px-2"
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAdd}
              disabled={loading}
              className="!text-xs !py-1 !px-2"
            >
              <Plus className="w-3 h-3 mr-1" />
              {t('tasks:checklist.actions.add')}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title={t('tasks:checklist.messages.deleteTitle')}
        message={t('tasks:checklist.messages.deleteConfirm')}
        variant="danger"
      />
    </div>
  );
}
